import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { strapiGet, strapiPost, strapiPut, strapiDelete } from '@/lib/strapi'
import type { FloorPlanLayout } from '@/lib/types/floorPlanLayout'
import { useAppStore } from '@/lib/store/useAppStore'
import { toast } from 'sonner'

export function useFloorPlanLayouts() {
  const restaurantId = useAppStore((s) => s.restaurantId)
  return useQuery({
    queryKey: ['floorPlanLayouts', restaurantId],
    queryFn: () =>
      strapiGet<FloorPlanLayout[]>('/api/floor-plan-layouts', {
        filters: { restaurant: { documentId: { $eq: restaurantId } } },
        sort: ['date:asc', 'startTime:asc'],
        pagination: { pageSize: 100 },
      }),
    enabled: !!restaurantId,
    staleTime: 5 * 60_000,
  })
}

export function useCreateFloorPlanLayout() {
  const qc = useQueryClient()
  const restaurantId = useAppStore((s) => s.restaurantId)
  return useMutation({
    mutationFn: (data: {
      name: string
      date?: string | null
      startTime?: string | null
      endTime?: string | null
      positions?: Record<string, { posX: number; posY: number; rotation: number }> | null
      disabledCombinations?: string[] | null
      localCombinations?: Array<{ tableDocumentIds: string[]; name: string; seatsMin: number; seatsMax: number }> | null
      autoGenerated?: boolean
    }) =>
      strapiPost<FloorPlanLayout>('/api/floor-plan-layouts', {
        data: { ...data, restaurant: restaurantId },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['floorPlanLayouts'] })
      toast.success('Layout override created')
    },
    onError: () => toast.error('Failed to create layout override'),
  })
}

export function useUpdateFloorPlanLayout() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      documentId,
      ...data
    }: {
      documentId: string
      name?: string
      positions?: Record<string, { posX: number; posY: number; rotation: number }>
      disabledCombinations?: string[] | null
      localCombinations?: Array<{ tableDocumentIds: string[]; name: string; seatsMin: number; seatsMax: number }> | null
      autoGenerated?: boolean
    }) => strapiPut<FloorPlanLayout>(`/api/floor-plan-layouts/${documentId}`, { data }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['floorPlanLayouts'] })
      toast.success('Layout saved')
    },
    onError: () => toast.error('Failed to save layout'),
  })
}

export function useDeleteFloorPlanLayout() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (documentId: string) => strapiDelete(`/api/floor-plan-layouts/${documentId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['floorPlanLayouts'] })
      toast.success('Layout override deleted')
    },
    onError: () => toast.error('Failed to delete layout override'),
  })
}
